import { useState, useCallback } from 'react';

// Fixed accent colours (Rathenau – first author) used for years & "Mehr lesen"
const ACCENT       = '#92400e'; // light mode
const ACCENT_DARK  = '#fbbf24'; // dark mode

const authors = [
  {
    id: 1,
    name: 'Walther Rathenau',
    years: '1867–1922',
    category: 'Industrieller · Schriftsteller · Politiker',
    image: '/authors/rathenau.jpg',
    bio: 'Walther Rathenau wurde am 29. September 1867 in Berlin als Sohn des jüdischen Industriellen Emil Rathenau geboren. Als Industrieller, Schriftsteller und liberaler Politiker zählte er zu den einflussreichsten Persönlichkeiten der Weimarer Republik. Er leitete den AEG-Konzern, verfasste bedeutende kulturkritische Schriften und diente als Reichsaußenminister. Am 24. Juni 1922 fiel er einem rechtsextremistischen Attentat in Berlin-Grunewald zum Opfer – eines der ersten politischen Morde der Weimarer Republik.',
    quote: '»Der nächste Krieg wird mit Steinen ausgefochten.«',
    works: ['Zur Kritik der Zeit (1912)', 'Zur Mechanik des Geistes (1913)', 'Von kommenden Dingen (1917)', 'Der neue Staat (1919)'],
    accent: '#92400e',
    accentDark: '#fbbf24',
    tag: 'Klassiker',
  },
  {
    id: 2,
    name: 'Rudolf Diesel',
    years: '1858–1913',
    category: 'Ingenieur · Erfinder · Schriftsteller',
    image: '/authors/diesel.jpg',
    bio: 'Rudolf Christian Karl Diesel, geboren am 18. März 1858 in Paris, war Ingenieur, Erfinder und visionärer Schriftsteller. Weltberühmt durch die Erfindung des Dieselmotors, schrieb er auch über Wirtschaft und Gesellschaft. Sein Werk „Solidarismus" (1903) entwarf eine utopische Wirtschaftsordnung. Am 29. September 1913 verschwand er unter rätselhaften Umständen auf der Überfahrt nach England – sein Tod im Ärmelkanal ist bis heute ungeklärt.',
    quote: '»Die Natur hat keine Abfälle, nur Rohstoffe für die nächste Verwendung.«',
    works: ['Theorie und Konstruktion eines rationellen Wärmemotors (1893)', 'Solidarismus (1903)', 'Die Entstehung des Dieselmotors (1913)'],
    accent: '#b45309',
    accentDark: '#fcd34d',
    tag: 'Klassiker',
  },
  {
    id: 3,
    name: 'Silvio Gesell',
    years: '1862–1930',
    category: 'Ökonom · Unternehmer · Schriftsteller',
    image: '/authors/gesell.jpg',
    bio: 'Johann Silvio Gesell wurde am 17. März 1862 in St. Vith (damals Preußen, heute Belgien) geboren. Als Unternehmer in Buenos Aires entwickelte er revolutionäre Wirtschaftstheorien. Er begründete die „Freiwirtschaft" und schlug das Konzept des „Freigeldes" (rostende Währung) vor – ein Geldsystem, das Horten unattraktiv macht. John Maynard Keynes würdigte ihn als „ungebührlich vernachlässigten Propheten". Gesell starb am 11. März 1930 in Oranienburg.',
    quote: '»Unser Wirtschaftssystem leidet nicht an zu viel, sondern an zu wenig Kapitalismus.«',
    works: ['Die Reformation im Münzwesen (1891)', 'Die natürliche Wirtschaftsordnung (1916)', 'Gesammelte Werke (14 Bände)'],
    accent: '#065f46',
    accentDark: '#34d399',
    tag: 'Klassiker', 
  },
  {
    id: 4,
    name: 'Harry Graf Kessler',
    years: '1868–1937',
    category: 'Kunstmäzen · Schriftsteller · Diplomat',
    image: '/authors/kessler.jpg',
    bio: 'Harry Clemens Ulrich Graf Kessler, geboren am 23. Mai 1868 in Paris, war eine der schillerndsten Figuren der Wilhelminischen und Weimarer Ära. Als Kunstsammler, Mäzen, Schriftsteller, Pazifist und Diplomat führte er 57 Jahre lang Tagebuch – ein einzigartiges kulturhistorisches Dokument. Er traf Thomas Mann, Auguste Rodin, Edvard Munch und zahllose Persönlichkeiten seiner Zeit. Er starb am 30. November 1937 im Exil in Lyon, verfolgt durch die Nationalsozialisten.',
    quote: '»Der Mensch lebt nicht von Brot allein, aber von Schönheit und Ideen auch.«',
    works: ['Das Tagebuch 1880–1937 (9 Bände)', 'Walther Rathenau – Sein Leben und Werk (1928)', 'Aus den Tagebüchern (1935)'],
    accent: '#5b21b6',
    accentDark: '#c4b5fd',
    tag: 'Klassiker',
  },
  {
    id: 5,
    name: 'Voltaire',
    years: '1694–1778',
    category: 'Philosoph · Dichter · Historiker',
    image: '/authors/voltaire.jpg',
    bio: 'François-Marie Arouet, bekannt unter dem Pseudonym Voltaire, wurde am 21. November 1694 in Paris geboren. Er ist der Inbegriff des aufklärerischen Geistes: streitbar, brillant, unerschrocken. In seiner 84-jährigen Schaffenszeit verfasste er über 2.000 Werke – Dramen, Gedichte, Romane, Essays und Briefe. Seine philosophische Erzählung „Candide" gilt als Meisterwerk der Weltliteratur. Er kämpfte zeitlebens gegen religiösen Fanatismus und politische Willkür. Sein Grabspruch: „Er lehrte uns, frei zu sein."',
    quote: '» Écrasez l’infâme!«',
    works: ['Candide (1759)', 'Lettres philosophiques (1734)', 'Zadig (1747)', 'Dictionnaire philosophique (1764)', 'L\'Ingénu (1767)'],
    accent: '#1d4ed8',
    accentDark: '#93c5fd',
    tag: 'Klassiker',
  },
  {
    id: 6,
    name: 'Johann Nikolaus Forkel',
    years: '1749–1818',
    category: 'Musikforscher · Organist · Autor',
    image: '/authors/Forkel.webp',
    bio: 'Johann Nikolaus Forkel wurde am 22. Februar 1749 in Meeder bei Coburg geboren und gilt als Begründer der modernen Musikwissenschaft. Als langjähriger Universitätsmusikrektor in Göttingen verfasste er die erste umfassende Musikgeschichte in deutscher Sprache sowie die erste wissenschaftliche Biographie Johann Sebastian Bachs (1802). Diese Bach-Monographie legte den Grundstein für die spätere Bach-Renaissance. Forkel starb am 20. März 1818 in Göttingen.',
    quote: '»Wer Bach nicht kennt, dem fehlt das Fundament wahrer Musik.«',
    works: ['Allgemeine Geschichte der Musik (1788–1801)', 'Über Johann Sebastian Bachs Leben, Kunst und Kunstwerke (1802)', 'Allgemeine Litteratur der Musik (1792)'],
    accent: '#9f1239',
    accentDark: '#fda4af',
    tag: 'Klassiker',
/*     imageNote: 'Illustratives Porträt (kein Originalfoto überliefert)', */
  },
  {
    id: 7,
    name: 'Sören Padel',
    years: '1964*',
    category: 'Autor · Lektor · Webentwickler',
    image: '/authors/padel.png',
    bio: 'Sören Padel ist ein in Berlin aufgewachsener und in Schweden lebender Autor und Lektor. Als Humangeograf (Bevölkerungsgeografie) und Historiker (historische Demografie) vermisste er ein Einführungswerk in die Demografie, welches sich an Nicht-Demografen wendet. Weder im Deutschen, Englischen oder Schwedischen gab es derlei. Also erstellte er es selber. Es ist bis heute in seiner Ausrichtung im deutschsprachigen Raum konkurrenzlos.  Außerdem ist er Lektor im Verlagsbetrieb von Vidento. Darüber hinaus war er als Übersetzer und Webentwickler und im journalistischen Bereich tätig,',
    quote: '»Die Wahrhaftigkeit einer Aussage erkennt man ein drei Punkten: Faktizität, Relevanz, Vollständigkeit. Hüte Dich vor denen, die durch Framen und Weglassen lügen!«',
    works: [ 'Einführung in die Demographie: Ein Überblick', 'Schwedische Krimonalfälle'],
    accent: '#0e7490',
    accentDark: '#67e8f9',
    tag: 'Demografie',
/*     imageNote: 'Illustratives Porträt (keine öffentliche Aufnahme verfügbar)', */
  },
];

// Light / Dark tag styles
const categoryColorsLight = {
  'Klassiker':               'bg-amber-100 text-amber-800 border border-amber-200',
  'Demografie':              'bg-blue-100 text-blue-800 border border-blue-200',
  'Kinder- und Jugendbücher':'bg-cyan-100 text-cyan-800 border border-cyan-200',
};
const categoryColorsDark = {
  'Klassiker':               'bg-amber-900/40 text-amber-300 border border-amber-700',
  'Demografie':              'bg-blue-900/40 text-blue-300 border border-blue-700',
  'Kinder- und Jugendbücher':'bg-cyan-900/40 text-cyan-300 border border-cyan-700',
};

function TagBadge({ tag }) {
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${categoryColorsLight[tag]} dark:hidden`}>
      {tag}
    </span>
  );
}
function TagBadgeDark({ tag }) {
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium hidden dark:inline ${categoryColorsDark[tag]}`}>
      {tag}
    </span>
  );
}

function AuthorImage({ author, className }) {
  const [failCount, setFailCount] = useState(0);
  const fallbacks = [
    author.image,
    `https://ui-avatars.com/api/?name=${encodeURIComponent(author.name)}&size=400&background=e7e5e4&color=78716c&bold=true&length=2`,
  ].filter(Boolean);

  const handleError = useCallback((e) => {
    const next = failCount + 1;
    if (next < fallbacks.length) {
      e.target.src = fallbacks[next];
      setFailCount(next);
    }
  }, [failCount, fallbacks]);

  return (
    <img src={fallbacks[failCount]} alt={author.name} className={className} onError={handleError} />
  );
}

function AuthorCard({ author, onClick }) {
  return (
    <div
      className="group cursor-pointer relative overflow-hidden rounded-2xl bg-white dark:bg-navy-800 border border-stone-200 dark:border-navy-700 hover:border-stone-300 dark:hover:border-stone-500 hover:shadow-xl dark:hover:shadow-navy-900 transition-all duration-300 hover:-translate-y-1"
      onClick={() => onClick(author)}
    >
      {/* Image */}
      <div className="relative h-72 overflow-hidden bg-stone-100 dark:bg-navy-700">
        <AuthorImage
          author={author}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/60 dark:from-navy-800/80 to-transparent" />

        {/* Tag */}
        <div className="absolute top-3 right-3">
          <TagBadge tag={author.tag} />
          <TagBadgeDark tag={author.tag} />
        </div>

        {/* Image note */}
        {author.imageNote && (
          <div className="absolute bottom-2 left-2 right-2">
            <span className="text-xs text-stone-500 dark:text-stone-400 italic">* {author.imageNote}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="mb-1">
          <span className="text-xs font-semibold tracking-wider uppercase dark:hidden" style={{ color: ACCENT }}>
            {author.years}
          </span>
          <span className="text-xs font-semibold tracking-wider uppercase hidden dark:inline" style={{ color: ACCENT_DARK }}>
            {author.years}
          </span>
        </div>
        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-1">{author.name}</h3>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-3">{author.category}</p>
        <p className="text-sm text-stone-600 dark:text-stone-300 line-clamp-3 leading-relaxed">
          {author.bio.substring(0, 140)}…
        </p>

        <div className="mt-4 flex items-center gap-1 text-sm font-semibold dark:hidden" style={{ color: ACCENT }}>
          <span>Mehr lesen</span>
          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <div className="mt-4 hidden dark:flex items-center gap-1 text-sm font-semibold" style={{ color: ACCENT_DARK }}>
          <span>Mehr lesen</span>
          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function AuthorModal({ author, onClose }) {
  if (!author) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/60 dark:bg-navy-950/80 backdrop-blur-sm" />
      <div
        className="relative z-10 max-w-2xl w-full bg-white dark:bg-navy-900 rounded-3xl overflow-hidden border border-stone-200 dark:border-navy-700 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header image */}
        <div className="relative h-64 overflow-hidden bg-stone-100 dark:bg-navy-800">
          <AuthorImage author={author} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white dark:from-navy-900 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/80 dark:bg-navy-800/80 hover:bg-white dark:hover:bg-navy-700 flex items-center justify-center text-stone-700 dark:text-stone-300 shadow transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Name overlay */}
          <div className="absolute bottom-4 left-6">
            <span className="text-sm font-semibold tracking-wider uppercase mb-1 block dark:hidden" style={{ color: ACCENT }}>
              {author.years}
            </span>
            <span className="text-sm font-semibold tracking-wider uppercase mb-1 hidden dark:block" style={{ color: ACCENT_DARK }}>
              {author.years}
            </span>
            <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100">{author.name}</h2>
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">{author.category}</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Quote */}
          <blockquote
            className="pl-4 border-l-4 italic text-stone-600 dark:text-stone-300 text-base leading-relaxed dark:hidden"
            style={{ borderColor: author.accent }}
          >
            {author.quote}
          </blockquote>
          <blockquote
            className="pl-4 border-l-4 italic text-stone-300 text-base leading-relaxed hidden dark:block"
            style={{ borderColor: author.accentDark }}
          >
            {author.quote}
          </blockquote>

          {/* Bio */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-stone-400 font-semibold mb-2">Biographie</h4>
            <p className="text-stone-700 dark:text-stone-300 leading-relaxed text-sm">{author.bio}</p>
            {author.imageNote && (
              <p className="text-xs text-stone-400 dark:text-stone-500 italic mt-2">* Bild: {author.imageNote}</p>
            )}
          </div>

          {/* Works */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-stone-400 font-semibold mb-3">Wichtige Werke</h4>
            <ul className="space-y-2">
              {author.works.map((work, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-700 dark:text-stone-300">
                  <span
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 dark:hidden"
                    style={{ backgroundColor: author.accent }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 hidden dark:inline-block"
                    style={{ backgroundColor: author.accentDark }}
                  />
                  {work}
                </li>
              ))}
            </ul>
          </div>

          {/* Tag */}
          <div className="pt-2 border-t border-stone-100 dark:border-navy-800">
            <TagBadge tag={author.tag} />
            <TagBadgeDark tag={author.tag} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthorsPage() {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('Alle');

  const tags = ['Alle', 'Klassiker', 'Demografie', 'Kinder- und Jugendbücher'];
  const filtered = filter === 'Alle' ? authors : authors.filter(a => a.tag === filter);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-navy-950 text-stone-800 dark:text-stone-200 transition-colors duration-300">

      {/* Hero */}
      <div className="bg-white dark:bg-navy-900 border-b border-stone-100 dark:border-navy-800">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-full px-4 py-2 text-sm text-amber-700 dark:text-amber-400 mb-6">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
            7 Autorenprofile · Literatur, Philosophie &amp; Wissenschaft
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold font-serif tracking-tight mb-4 text-stone-800 dark:text-stone-100">
            Unsere Autoren
          </h1>
          <p className="text-xl text-stone-500 dark:text-stone-400 max-w-2xl mx-auto leading-relaxed">
            Denker, Erfinder, Philosophen und Chronisten – Menschen, deren Worte die Welt veränderten
            und noch heute nachwirken.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-4">
        <div className="flex flex-wrap gap-2">
          {tags.map(tag => (
            <button
              key={tag}
              onClick={() => setFilter(tag)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                filter === tag
                  ? 'bg-stone-800 dark:bg-amber-400 text-white dark:text-navy-950 shadow'
                  : 'bg-white dark:bg-navy-800 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-navy-700 hover:border-stone-400 dark:hover:border-stone-500 hover:text-stone-800 dark:hover:text-stone-100'
              }`}
            >
              {tag}
              <span className="ml-2 text-xs opacity-60">
                ({tag === 'Alle' ? authors.length : authors.filter(a => a.tag === tag).length})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Author grid */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
          {filtered.map(author => (
            <AuthorCard key={author.id} author={author} onClick={setSelected} />
          ))}
        </div>


      </div>

      {/* Modal */}
      {selected && <AuthorModal author={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
